import { index, modelOptions, mongoose, PropType, post, prop } from "@typegoose/typegoose";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";

const SCHEMA_KEY = "SentDownlink" as const;

type FieldsWithDefaults =
    | "schemaKey"
    | "_id"
    | "flaggedAsReceived"
    | "flaggedAsFailed"
    | "confirmed"
    | "correlationIds";

// Sliding window reads
@index({ node_id: 1, date: -1 })
// Upsert key: re-using an idemp token replaces the existing row
@index({ node_id: 1, idemp: 1 }, { unique: true })
@modelOptions({
    schemaOptions: {
        collection: "sentdownlinks",
        timestamps: true,
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
    },
})
@post<SchemaBase<SentDownlink>>(...addSchemaKeyHook(SCHEMA_KEY))
// No `@validate` decorators, and so no `dtoValidators`: rows are written only by the device
// communication layer from values it already holds, and no endpoint accepts or returns them. Adding
// validators would generate Create/Update DTOs for a shape no client can send. Matches
// Gen4InternalStateTracker; contrast ResponseTimeStats, which is API-exposed and does validate.
export class SentDownlink extends SchemaBase<SentDownlink, FieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;
    readonly schemaKey = SCHEMA_KEY;

    public _id!: mongoose.Types.ObjectId;

    @prop({ type: () => String, required: true })
    public node_id!: string;

    /**
     * The idemp token sent with this downlink. The device echoes it back to acknowledge.
     */
    @prop({ type: () => Number, required: true })
    public idemp!: number;

    @prop({ type: () => Number, required: true })
    public fPort!: number;

    @prop({ type: () => Number, required: true }, PropType.ARRAY)
    public bytes!: number[];

    /**
     * When the downlink was sent.
     */
    @prop({ type: () => Date, required: true })
    public date!: Date;

    /**
     * Set once the downlink is acknowledged and we stop listening for it.
     */
    @prop({ type: () => Boolean, required: true, default: false })
    flaggedAsReceived: boolean = false;

    /**
     * Set once we are confident the downlink failed and we stop listening for it.
     */
    @prop({ type: () => Boolean, required: true, default: false })
    flaggedAsFailed: boolean = false;

    /**
     * The SproutLogger document describing why this downlink was sent.
     */
    @prop({ type: () => String, required: false })
    public interactionID?: string;

    /**
     * Whether we asked TTI to send this downlink confirmed.
     */
    @prop({ type: () => Boolean, required: true, default: false })
    confirmed: boolean = false;

    /**
     * TTI's own correlation IDs for this downlink. Not populated yet.
     */
    @prop({ type: () => String, required: true, default: [] }, PropType.ARRAY)
    public correlationIds: string[] = [];

    // Lifecycle timestamps, one per TTI downlink event. Nullable and unordered: TTI delivers webhooks
    // at least once and out of order, so each is set independently and never derived from the others.
    // Nothing populates these until the downlink event webhooks are ingested.

    @prop({ type: () => Date, required: false })
    public ttiQueuedAt?: Date;

    @prop({ type: () => Date, required: false })
    public ttiSentAt?: Date;

    /** Only ever set for downlinks sent with `confirmed`, so a null here is not evidence of failure. */
    @prop({ type: () => Date, required: false })
    public ttiAckedAt?: Date;

    @prop({ type: () => Date, required: false })
    public ttiNackedAt?: Date;

    @prop({ type: () => Date, required: false })
    public ttiFailedAt?: Date;

    /** Set when a later downlink replaced the queue before this one was sent. */
    @prop({ type: () => Date, required: false })
    public ttiInvalidatedAt?: Date;

    /**
     * When the device echoed this downlink's idemp token. Distinct from `ttiAckedAt`: that is a
     * MAC-layer confirmation the radio received the frame, this is evidence the payload was applied.
     */
    @prop({ type: () => Date, required: false })
    public deviceEchoedAt?: Date;

    /**
     * Managed by `timestamps`, declared so it is readable. Reset on every write, including the
     * replacement of a row whose idemp token was re-used, so it always dates the current row.
     */
    @prop({ type: () => Date, required: false })
    public createdAt?: Date;

    static getters = {} as const satisfies Record<string, (self: SentDownlink) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    constructor(properties?: SchemaConstructorArgs<SentDownlink, FieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}
